import { Injectable, ElementRef } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ScrollOptions, ScrollState, ScrollCallbacks } from '../../models/scroll.models';
import { LoggerService } from 'src/services/logger-services/logger.service';

/**
 * Enhanced shared service for managing auto-scroll functionality
 *
 * Supports:
 * - User scroll detection
 * - Scroll-to-bottom arrow
 * - Near-bottom detection
 * - Auto-scroll during streaming
 * - Fully configurable with loose coupling via options and callbacks
 *
 * To interrupt auto-scroll during streaming: scroll UP by more than SCROLL_UP_THRESHOLD_PX (1px).
 * Used by agent and chatbot interfaces to manage scroll behavior
 */
/** Min scroll-up (px) to treat as user interrupt. Use 30 to avoid false interrupt from jitter/timing. */
const SCROLL_UP_THRESHOLD_PX = 10;

@Injectable({
  providedIn: 'root'
})
export class ChatAutoScrollService {
  private chatContainer: HTMLElement | null = null;
  private autoScrollTimeout: any = null;
  /** When scroll runs with no content (maxScroll 0), we schedule a retry so slow-render threads still get to bottom */
  private lateScrollRetryTimeout: any = null;
  private lastAutoScrollTime: number = 0;
  private lastScrollHeight = 0;
  private lastContentGrowthTime = 0;

  private scrollState: ScrollState = {
    userHasScrolledUp: false,
    isNearBottom: true,
    showScrollToBottomArrow: false,
    lastScrollTop: 0,
    isAutoScrolling: false
  };

  private options: ScrollOptions = {
    enableUserScrollDetection: false,
    enableScrollArrow: false,
    enableNearBottomDetection: false,
    throttleMs: 100,
    nearBottomThreshold: 100,
    scrollArrowThreshold: 300
  };

  private callbacks: ScrollCallbacks = {};

  private readonly scrollStateSubject = new BehaviorSubject<ScrollState>(this.scrollState);
  public scrollState$ = this.scrollStateSubject.asObservable();

  constructor(private readonly logger: LoggerService) {}

  /**
   * Configures scroll behavior options
   * @param options - Configuration options
   */
  configure(options: Partial<ScrollOptions>): void {
    try {
      this.options = { ...this.options, ...options };
    } catch (error: any) {
      this.logger.logInfo(`Error in configure: ${error?.message || error}`);
    }
  }

  /**
   * Sets callbacks for custom behavior
   * @param callbacks - Callback functions
   */
  setCallbacks(callbacks: ScrollCallbacks): void {
    try {
      this.callbacks = { ...this.callbacks, ...callbacks };
    } catch (error: any) {
      this.logger.logInfo(`Error in setCallbacks: ${error?.message || error}`);
    }
  }

  /**
   * Gets current scroll state
   * @returns Current scroll state
   */
  getScrollState(): ScrollState {
    try {
      return { ...this.scrollState };
    } catch (error: any) {
      this.logger.logInfo(`Error in getScrollState: ${error?.message || error}`);
      return this.scrollState;
    }
  }

  /**
   * Gets scroll state as observable
   * @returns Observable of scroll state
   */
  getScrollStateObservable(): Observable<ScrollState> {
    return this.scrollState$;
  }

  /**
   * Updates scroll state and notifies subscribers
   * @param updates - Partial state updates
   */
  private updateScrollState(updates: Partial<ScrollState>): void {
    try {
      const previousState = { ...this.scrollState };
      this.scrollState = { ...this.scrollState, ...updates };
      this.scrollStateSubject.next(this.scrollState);

      if (this.options.enableNearBottomDetection && 
          previousState.isNearBottom !== this.scrollState.isNearBottom &&
          this.callbacks.onNearBottomChange) {
        this.callbacks.onNearBottomChange(this.scrollState.isNearBottom);
      }

      if (this.options.enableScrollArrow && 
          previousState.showScrollToBottomArrow !== this.scrollState.showScrollToBottomArrow &&
          this.callbacks.onScrollArrowChange) {
        this.callbacks.onScrollArrowChange(this.scrollState.showScrollToBottomArrow);
      }
    } catch (error: any) {
      this.logger.logInfo(`Error in updateScrollState: ${error?.message || error}`);
    }
  }

  /**
   * Resets scroll state to defaults
   * @param customState - Optional custom state values
   */
  resetScrollState(customState?: Partial<ScrollState>): void {
    try {
      const defaultState: ScrollState = {
        userHasScrolledUp: false,
        isNearBottom: true,
        showScrollToBottomArrow: false,
        lastScrollTop: 0,
        isAutoScrolling: false
      };
      this.updateScrollState({ ...defaultState, ...customState });
    } catch (error: any) {
      this.logger.logInfo(`Error in resetScrollState: ${error?.message || error}`);
    }
  }

  /**
   * Handles user scroll event
   * Should be called from host component's scroll event
   * Uses event.target when present so we read from the element that actually scrolled (reliable interrupt).
   * @param event - Scroll event
   */
  handleUserScroll(event?: Event): void {
    try {
      if (!this.options.enableUserScrollDetection && !this.options.enableScrollArrow && !this.options.enableNearBottomDetection) {
        return;
      }

      const scrollSource = (event?.target as HTMLElement) || this.chatContainer;
      if (!scrollSource) {
        return;
      }
      if (scrollSource !== this.chatContainer) {
        this.chatContainer = scrollSource;
      }

      const currentScrollTop = scrollSource.scrollTop;
      const scrollHeight = scrollSource.scrollHeight;
      const clientHeight = scrollSource.clientHeight;
      const maxScrollTop = scrollHeight - clientHeight;
      const distanceFromBottom = maxScrollTop - currentScrollTop;
      const nearBottomThresholdPx = this.options.nearBottomThreshold || 100;
      /** User clearly moved up to read history — honor interrupt even if layout is still growing (Via/markdown). */
      const userClearlyAwayFromBottom = distanceFromBottom > nearBottomThresholdPx + 50;

      // During auto-scroll: detect user scroll-up. User must scroll UP by more than SCROLL_UP_THRESHOLD_PX to interrupt.
      if (this.scrollState.isAutoScrolling) {
        if (this.options.enableUserScrollDetection) {
          const scrollUpThreshold = SCROLL_UP_THRESHOLD_PX;
          const isScrollingUp = currentScrollTop < this.scrollState.lastScrollTop - scrollUpThreshold;
          
          // During rapid streaming (fast content growth), scrollTop can lag behind programmatic set; avoid false interrupt
          const contentGrowth = scrollHeight - this.lastScrollHeight;
          const timeSinceLastGrowth = Date.now() - this.lastContentGrowthTime;
          const isRapidGrowth = contentGrowth > 100 && timeSinceLastGrowth < 200;
          this.lastScrollHeight = scrollHeight;
          this.lastContentGrowthTime = Date.now();

          if (isScrollingUp && (!isRapidGrowth || userClearlyAwayFromBottom)) {
            const nearBottom = maxScrollTop - currentScrollTop < (this.options.nearBottomThreshold || 100);
            const positionAwayFromBottom = currentScrollTop < maxScrollTop - (this.options.scrollArrowThreshold || 300);
            const showArrow = this.options.enableScrollArrow
              ? positionAwayFromBottom
              : this.scrollState.showScrollToBottomArrow;

            this.updateScrollState({
              userHasScrolledUp: true,
              isAutoScrolling: false,
              isNearBottom: nearBottom,
              showScrollToBottomArrow: showArrow,
              lastScrollTop: currentScrollTop
            });

            if (this.autoScrollTimeout) {
              clearTimeout(this.autoScrollTimeout);
              this.autoScrollTimeout = null;
            }

            if (this.callbacks.onUserScroll) {
              this.callbacks.onUserScroll(this.scrollState);
            }
          } else {
            this.scrollState.lastScrollTop = currentScrollTop;
          }
        }
        return;
      }

      if (this.options.enableUserScrollDetection) {
        const scrollThreshold = SCROLL_UP_THRESHOLD_PX;
        const isScrollingUp = currentScrollTop < this.scrollState.lastScrollTop - scrollThreshold;

        // Also check rapid growth here to avoid false interrupt when scroll event is delayed
        const contentGrowth = scrollHeight - this.lastScrollHeight;
        const timeSinceLastGrowth = Date.now() - this.lastContentGrowthTime;
        const isRapidGrowth = contentGrowth > 100 && timeSinceLastGrowth < 200;
        
        // Update tracking even in this path
        if (!this.scrollState.isAutoScrolling) {
          this.lastScrollHeight = scrollHeight;
          this.lastContentGrowthTime = Date.now();
        }

        if (isScrollingUp && (!isRapidGrowth || userClearlyAwayFromBottom)) {
          if (!this.scrollState.userHasScrolledUp) {
            this.updateScrollState({ userHasScrolledUp: true, isAutoScrolling: false });
            if (this.autoScrollTimeout) {
              clearTimeout(this.autoScrollTimeout);
              this.autoScrollTimeout = null;
            }
          }
        }
      }

      if (this.options.enableNearBottomDetection) {
        const nearBottomThresholdPx = this.options.nearBottomThreshold || 100;
        const nearBottom = maxScrollTop - currentScrollTop < nearBottomThresholdPx;
        if (nearBottom !== this.scrollState.isNearBottom) {
          this.updateScrollState({ isNearBottom: nearBottom });
        }
        // Clear userHasScrolledUp when user scrolls back to bottom so auto-scroll resumes during streaming.
        const distanceFromBottom = maxScrollTop - currentScrollTop;
        const isNearBottomForClear = distanceFromBottom <= nearBottomThresholdPx;
        const scrollingDown = currentScrollTop > this.scrollState.lastScrollTop;
        const scrollDownDelta = currentScrollTop - this.scrollState.lastScrollTop;
        // Allow clear on small downward movement (15px) when near bottom so scroll-back in multiple steps still resets.
        const minScrollDownToClearPx = 15;
        if (
          isNearBottomForClear &&
          scrollingDown &&
          scrollDownDelta >= minScrollDownToClearPx &&
          this.scrollState.userHasScrolledUp &&
          !this.scrollState.isAutoScrolling
        ) {
          this.updateScrollState({ userHasScrolledUp: false });
          // Notify so interface can restart streaming scroll interval (same as scroll-to-bottom arrow click).
          if (this.callbacks.onScrollToBottom) {
            this.callbacks.onScrollToBottom();
          }
        }
      }

      if (this.options.enableScrollArrow) {
        const positionAwayFromBottom = currentScrollTop < maxScrollTop - (this.options.scrollArrowThreshold || 300);
        // When user scroll detection is on (all agents): show arrow only after user has scrolled up (chatbot-like, no jerk on load)
        const showArrow = positionAwayFromBottom && (
          !this.options.enableUserScrollDetection || this.scrollState.userHasScrolledUp
        );
        if (showArrow !== this.scrollState.showScrollToBottomArrow) {
          this.updateScrollState({ showScrollToBottomArrow: showArrow });
        }
      }

      this.updateScrollState({ lastScrollTop: currentScrollTop });

      if (this.callbacks.onUserScroll) {
        this.callbacks.onUserScroll(this.scrollState);
      }
    } catch (error: any) {
      this.logger.logInfo(`Error in handleUserScroll: ${error?.message || error}`);
    }
  }

  /**
   * Finds the scrollable chat container
   * @param selectors - Array of CSS selectors to try (default: common chat containers)
   * @param scrollTarget - Optional scroll target element reference
   * @returns The found HTMLElement or null
   */
  findChatContainer(
    selectors: string[] = [
      '.agent-chat-container',
      '.agent-chat-history-wrapper',
      '.chat-expandable-area-new',
      '.chat-right-box'
    ],
    scrollTarget?: ElementRef
  ): HTMLElement | null {
    try {
      // Prefer agent chat scroll target when no ref (avoids picking chatbot's scrolltobottom when both exist)
      const scrollElement = scrollTarget?.nativeElement
        || document.getElementById('agent-chat-scrolltobottom')
        || document.getElementById('scrolltobottom');
      if (scrollElement) {
        let parent = scrollElement.parentElement;
        while (parent && parent !== document.body) {
          const style = window.getComputedStyle(parent);
          if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
            this.chatContainer = parent;
            return this.chatContainer;
          }
          parent = parent.parentElement;
        }
      }

      for (const selector of selectors) {
        const container = document.querySelector(selector);
        if (container) {
          const element = container as HTMLElement;
          const style = window.getComputedStyle(element);
          if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
            this.chatContainer = element;
            return this.chatContainer;
          }
        }
      }

      return null;
    } catch (error: any) {
      this.logger.logInfo(`Error in findChatContainer: ${error?.message || error}`);
      return null;
    }
  }

  /**
   * Auto-scrolls during streaming responses
   * Uses gentle incremental scrolling to avoid jarring user experience
   * Respects user scroll detection and near-bottom detection if enabled
   * @param isLoading - Whether content is currently loading/streaming
   * @param throttleMs - Throttle time in milliseconds (uses configured value if not provided)
   */
  autoScrollDuringStreaming(
    isLoading: boolean = false,
    throttleMs?: number
  ): void {
    try {
      if (!isLoading) {
        return;
      }

      if (this.options.enableUserScrollDetection && this.scrollState.userHasScrolledUp) {
        return;
      }

      // Resolve container first so position check reads the actual scrollable element
      if (!this.chatContainer) {
        this.findChatContainer();
      }
      if (!this.chatContainer) {
        return;
      }

      // Do NOT set userHasScrolledUp here when away from bottom: that can happen because new content
      // grew (user was at bottom), and we must keep auto-scrolling. Only handleUserScroll sets userHasScrolledUp.

      if (this.options.enableNearBottomDetection && !this.scrollState.isNearBottom) {
        return;
      }

      const actualThrottle = throttleMs ?? this.options.throttleMs ?? 100;

      const now = Date.now();
      if (this.lastAutoScrollTime && now - this.lastAutoScrollTime < actualThrottle) {
        return;
      }

      this.lastAutoScrollTime = now;
      this.updateScrollState({ isAutoScrolling: true });

      requestAnimationFrame(() => {
        if (this.options.enableUserScrollDetection && this.scrollState.userHasScrolledUp) {
          this.updateScrollState({ isAutoScrolling: false });
          return;
        }

        this.performGentleScroll();
        // isAutoScrolling is cleared inside performGentleScroll after 150ms so scroll events from our scroll don't clear userHasScrolledUp

        if (this.callbacks.onAutoScroll) {
          this.callbacks.onAutoScroll(this.scrollState);
        }
      });
    } catch (error: any) {
      this.logger.logInfo(`Error in autoScrollDuringStreaming: ${error?.message || error}`);
      this.updateScrollState({ isAutoScrolling: false });
    }
  }

  /**
   * Performs gentle/incremental scroll
   * Scrolls incrementally if far from bottom, jumps to bottom if close.
   * Sets isAutoScrolling so the resulting scroll event does not clear userHasScrolledUp.
   */
  private performGentleScroll(): void {
    try {
      if (this.chatContainer) {
        const currentScrollTop = this.chatContainer.scrollTop;
        const scrollHeight = this.chatContainer.scrollHeight;
        const clientHeight = this.chatContainer.clientHeight;
        const maxScrollTop = scrollHeight - clientHeight;

        if (this.autoScrollTimeout) {
          clearTimeout(this.autoScrollTimeout);
          this.autoScrollTimeout = null;
        }
        this.updateScrollState({ isAutoScrolling: true });

        if (maxScrollTop - currentScrollTop < 100) {
          this.chatContainer.scrollTop = maxScrollTop;
          this.updateScrollState({ lastScrollTop: maxScrollTop });
        } else {
          const targetScroll = Math.min(currentScrollTop + 50, maxScrollTop);
          this.chatContainer.scrollTop = targetScroll;
          this.updateScrollState({ lastScrollTop: targetScroll });
        }
        this.autoScrollTimeout = setTimeout(() => {
          this.autoScrollTimeout = null;
          this.updateScrollState({ isAutoScrolling: false });
        }, 300);
        return;
      }

      const scrollElement = document.getElementById('agent-chat-scrolltobottom') || document.getElementById('scrolltobottom');
      if (scrollElement) {
        if (this.autoScrollTimeout) clearTimeout(this.autoScrollTimeout);
        this.updateScrollState({ isAutoScrolling: true });
        scrollElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
        this.autoScrollTimeout = setTimeout(() => {
          this.autoScrollTimeout = null;
          this.updateScrollState({ isAutoScrolling: false });
        }, 300);
      }
    } catch (error: any) {
      this.logger.logInfo(`Error in performGentleScroll: ${error?.message || error}`);
      this.updateScrollState({ isAutoScrolling: false });
    }
  }

  /**
   * Scrolls to bottom of chat container
   * @param behavior - 'smooth' for animated scroll, 'auto' for instant jump
   */
  scrollToBottom(behavior: 'smooth' | 'auto' = 'smooth'): void {
    try {
      // Clear follow-pause immediately so chunk handlers (e.g. triggerAutoScroll on the same turn as SSE)
      // do not see stale userHasScrolledUp while smooth scroll is still deferred (setTimeout/rAF).
      this.updateScrollState({
        userHasScrolledUp: false,
        showScrollToBottomArrow: false
      });
      // For 'auto' (streaming): scroll synchronously when we have the container so we don't race with
      // scroll events — deferred scroll let a scroll event fire with old position and we set userHasScrolledUp by mistake.
      if (behavior === 'auto') {
        if (!this.chatContainer) {
          this.findChatContainer();
        }
        if (this.chatContainer) {
          if (this.autoScrollTimeout) clearTimeout(this.autoScrollTimeout);
          this.updateScrollState({ isAutoScrolling: true });
          const maxScroll = this.chatContainer.scrollHeight - this.chatContainer.clientHeight;
          this.chatContainer.scrollTop = maxScroll;
          const contentNotReady = maxScroll <= 0 || (maxScroll < 100 && this.chatContainer.scrollHeight < 1000);
          if (contentNotReady) {
            if (this.lateScrollRetryTimeout) clearTimeout(this.lateScrollRetryTimeout);
            this.lateScrollRetryTimeout = setTimeout(() => {
              this.lateScrollRetryTimeout = null;
              if (this.scrollState.userHasScrolledUp) {
                return;
              }
              this.findChatContainer();
              this.scrollToBottom('auto');
            }, 800);
          }
          this.updateScrollState({
            isNearBottom: true,
            userHasScrolledUp: false,
            showScrollToBottomArrow: false,
            lastScrollTop: maxScroll
          });
          if (this.callbacks.onScrollToBottom) {
            this.callbacks.onScrollToBottom();
          }
          this.autoScrollTimeout = setTimeout(() => {
            this.autoScrollTimeout = null;
            this.updateScrollState({ isAutoScrolling: false });
            if (this.chatContainer) {
              const currentScroll = this.chatContainer.scrollTop;
              const expectedScroll = this.chatContainer.scrollHeight - this.chatContainer.clientHeight;
              if (Math.abs(currentScroll - expectedScroll) > 10) {
                this.chatContainer.scrollTop = expectedScroll;
              }
            }
          }, 300);
        }
        return;
      }

      // Resolve container immediately so deferred block has it (avoids "sometimes doesn't work").
      if (!this.chatContainer) {
        this.findChatContainer();
      }

      setTimeout(() => {
        if (!this.chatContainer) {
          this.findChatContainer();
        }

        if (this.chatContainer) {
          requestAnimationFrame(() => {
            if (this.chatContainer) {
              const maxScroll = this.chatContainer.scrollHeight - this.chatContainer.clientHeight;
              if (this.autoScrollTimeout) clearTimeout(this.autoScrollTimeout);
              this.updateScrollState({ isAutoScrolling: true });

              this.chatContainer.scrollTo({
                top: maxScroll,
                behavior: 'smooth'
              });

              this.updateScrollState({
                isNearBottom: true,
                userHasScrolledUp: false,
                showScrollToBottomArrow: false,
                lastScrollTop: maxScroll
              });

              if (this.callbacks.onScrollToBottom) {
                this.callbacks.onScrollToBottom();
              }

              this.autoScrollTimeout = setTimeout(() => {
                this.autoScrollTimeout = null;
                this.updateScrollState({ isAutoScrolling: false });
                if (!this.chatContainer) return;
                const currentScroll = this.chatContainer.scrollTop;
                const expectedScroll = this.chatContainer.scrollHeight - this.chatContainer.clientHeight;
                if (Math.abs(currentScroll - expectedScroll) > 10) {
                  this.chatContainer.scrollTop = expectedScroll;
                }
              }, 300);
            }
          });
          return;
        }

        const scrollElement = document.getElementById('agent-chat-scrolltobottom') || document.getElementById('scrolltobottom');
        if (scrollElement) {
          let scrollableParent = scrollElement.parentElement;
          while (scrollableParent && scrollableParent !== document.body) {
            const style = window.getComputedStyle(scrollableParent);
            if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
              const maxScroll = scrollableParent.scrollHeight - scrollableParent.clientHeight;
              if (this.autoScrollTimeout) clearTimeout(this.autoScrollTimeout);
              this.updateScrollState({ isAutoScrolling: true });
              scrollableParent.scrollTop = maxScroll;
              this.updateScrollState({
                isNearBottom: true,
                userHasScrolledUp: false,
                showScrollToBottomArrow: false
              });
              this.autoScrollTimeout = setTimeout(() => {
                this.autoScrollTimeout = null;
                this.updateScrollState({ isAutoScrolling: false });
              }, 300);
              
              if (this.callbacks.onScrollToBottom) {
                this.callbacks.onScrollToBottom();
              }
              return;
            }
            scrollableParent = scrollableParent.parentElement;
          }
          
          if (this.autoScrollTimeout) clearTimeout(this.autoScrollTimeout);
          this.updateScrollState({
            isAutoScrolling: true,
            userHasScrolledUp: false,
            showScrollToBottomArrow: false,
            isNearBottom: true
          });
          scrollElement.scrollIntoView({ 
            behavior: behavior === 'smooth' ? 'smooth' : 'auto', 
            block: 'end' 
          });
          if (this.callbacks.onScrollToBottom) {
            this.callbacks.onScrollToBottom();
          }
          this.autoScrollTimeout = setTimeout(() => {
            this.autoScrollTimeout = null;
            this.updateScrollState({ isAutoScrolling: false });
          }, 300);
        }
      }, 0);
    } catch (error: any) {
      this.logger.logInfo(`Error in scrollToBottom: ${error?.message || error}`);
    }
  }

  /**
   * Scrolls to bottom immediately without animation
   */
  scrollToBottomImmediate(): void {
    this.scrollToBottom('auto');
  }

  /**
   * Scrolls to bottom with smooth animation
   */
  scrollToBottomSmooth(): void {
    this.scrollToBottom('smooth');
  }

  /**
   * Cleans up timeouts and resets state
   * Should be called in ngOnDestroy
   * @param resetState - Whether to reset scroll state (default: false)
   */
  cleanup(resetState: boolean = false): void {
    try {
      if (this.autoScrollTimeout) {
        clearTimeout(this.autoScrollTimeout);
        this.autoScrollTimeout = null;
      }
      if (this.lateScrollRetryTimeout) {
        clearTimeout(this.lateScrollRetryTimeout);
        this.lateScrollRetryTimeout = null;
      }
      this.chatContainer = null;
      this.lastAutoScrollTime = 0;
      
      if (resetState) {
        this.resetScrollState();
      } else {
        this.updateScrollState({ isAutoScrolling: false });
      }
    } catch (error: any) {
      this.logger.logInfo(`Error in cleanup: ${error?.message || error}`);
    }
  }

  /**
   * Checks if currently auto-scrolling
   * @returns true if auto-scroll is in progress
   */
  isCurrentlyAutoScrolling(): boolean {
    return this.scrollState.isAutoScrolling;
  }

  /**
   * Gets the current chat container element
   * @returns The HTMLElement or null if not found
   */
  getChatContainer(): HTMLElement | null {
    return this.chatContainer;
  }

  /**
   * Returns true if the user has scrolled away from the bottom (position-based check).
   * Use this in addition to userHasScrolledUp to avoid racing with scroll events:
   * when an SSE chunk runs before the scroll event is processed, we still skip auto-scroll.
   * @param thresholdPx - Distance from bottom (px) below which we consider "at bottom". Default 100.
   */
  isUserAwayFromBottom(thresholdPx: number = 100): boolean {
    let el = this.chatContainer;
    if (!el) {
      this.findChatContainer();
      el = this.chatContainer;
    }
    if (!el) {
      return false;
    }
    const maxScrollTop = el.scrollHeight - el.clientHeight;
    if (maxScrollTop <= 0) return false;
    const distanceFromBottom = maxScrollTop - el.scrollTop;
    return distanceFromBottom > thresholdPx;
  }

  /**
   * Manually sets the chat container
   * Useful when you know the container element
   * @param container - The HTMLElement to use as scroll container
   */
  setChatContainer(container: HTMLElement): void {
    try {
      this.chatContainer = container;
    } catch (error: any) {
      this.logger.logInfo(`Error in setChatContainer: ${error?.message || error}`);
    }
  }
}

